import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Menu,
  ListItemIcon,
  ListItemText,
  IconButton,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PageLayout from "../../components/layout/PageLayout";
import Can from "../../components/permissions/Can";
import { useEffect, useMemo, useState } from "react";
import { extractPagination } from "../../utils/response";
import {
  useDeleteServicePlanMutation,
  useGetServicePlansQuery,
} from "../../store/api/servicePlansApi";
import { toast } from "react-toastify";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  SERVICE_PLANS_CREATE_PATH,
  SERVICE_PLANS_EDIT_PATH,
  SERVICE_PLANS_VIEW_PATH,
} from "../../navigations/path";
import SweetAlert from "../../utils/sweetAlert";
import { buildServicePlanRelationParams } from "./servicePlanApiHelpers";

const formatPrice = (value) => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }
  const price = Number(value);
  if (Number.isNaN(price)) {
    return value;
  }
  return `$${price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const ServicePlans = () => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
  });
  // =========================
  // QUERY PARAMS
  // =========================
  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      per_page: pagination.per_page,
      paginate: 1,
      ...buildServicePlanRelationParams(),
    }),
    [pagination.page, pagination.per_page],
  );
  // =========================
  // API CALL
  // =========================
  const {
    data,
    isFetching: loading,
    error,
  } = useGetServicePlansQuery(queryParams);

  const [deleteServicePlan] = useDeleteServicePlanMutation();
  // =========================
  // API RESPONSE LIST
  // =========================
  const rows = useMemo(() => {
    const list =
      data?.data?.list ??
      data?.list ??
      (Array.isArray(data?.data) ? data.data : []);
    return Array.isArray(list) ? list : [];
  }, [data]);
  // =========================
  // PAGINATION
  // =========================
  useEffect(() => {
    if (!data) return;
    const pag = extractPagination(data);
    setPagination((prev) => ({
      ...prev,
      total: pag.total ?? 0,
    }));
  }, [data]);
  // =========================
  // ERROR
  // =========================
  useEffect(() => {
    if (error) {
      toast.error(error?.data?.message || "Failed to load service plans");
    }
  }, [error]);
  // =========================
  // CHANGE PAGE
  // =========================
  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({
      ...prev,
      page: newPage + 1,
    }));
  };
  // =========================
  // CHANGE ROWS PER PAGE
  // =========================
  const handleChangeRowsPerPage = (event) => {
    const perPage = parseInt(event.target.value, 10) || 10;

    setPagination((prev) => ({
      ...prev,
      page: 1,
      per_page: perPage,
    }));
  };
  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };
  const goEdit = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(SERVICE_PLANS_EDIT_PATH.replace(":id", id));
  };
  const goView = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(SERVICE_PLANS_VIEW_PATH.replace(":id", id));
  };

  const handleDelete = async () => {
    const row = selectedRow;
    if (!row?.id) return;
    handleCloseMenu();
    const result = await SweetAlert.confirm({
      title: "Delete service plan?",
      text: "This action cannot be undone.",
      confirmButtonText: "Delete",
      icon: "warning",
    });
    if (!result.isConfirmed) return;

    setActionLoadingId(row.id);
    try {
      await deleteServicePlan(row.id).unwrap();
      toast.success("Service plan deleted");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete service plan");
    } finally {
      setActionLoadingId(null);
    }
  };
  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* ================= HEADER ================= */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: "primary.main", fontWeight: 600 }}
          >
            Service Plans
          </Typography>
          <Can permission="service-create">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate(SERVICE_PLANS_CREATE_PATH)}
            >
              Add Service Plan
            </Button>
          </Can>
        </Box>
        {/* ================= TABLE ================= */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ overflowX: "auto" }}
            >
              <Table size="small">
                {/* TABLE HEADER */}
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.100" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: 72, textAlign: "center" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                {/* TABLE BODY */}
                <TableBody>
                  {/* LOADING */}
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    /* EMPTY STATE */
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No service plans found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    /* DATA */
                    rows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() =>
                          row.id &&
                          navigate(
                            SERVICE_PLANS_VIEW_PATH.replace(":id", row.id),
                          )
                        }
                        sx={{ cursor: row.id ? "pointer" : "default" }}
                        aria-label="View service plan details"
                      >
                        <TableCell>{row.name ?? "—"}</TableCell>
                        <TableCell>{formatPrice(row.price)}</TableCell>
                        <TableCell>{row.description ?? "—"}</TableCell>
                        <TableCell>{row.status?.name ?? "—"}</TableCell>
                        <TableCell>{row.created_at ?? "—"}</TableCell>
                        <TableCell
                          onClick={(e) => e.stopPropagation()}
                          sx={{ textAlign: "center" }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenMenu(e, row)}
                            disabled={actionLoadingId === row.id}
                            aria-label="Actions"
                          >
                            {actionLoadingId === row.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <MoreVertIcon />
                            )}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* ================= PAGINATION ================= */}
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Can permission="service-view">
          <MenuItem onClick={goView}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="service-edit">
          <MenuItem onClick={goEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        </Can>
        <Can permission="service-delete">
          <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Can>
      </Menu>
    </PageLayout>
  );
};

export default ServicePlans;
